/* The chain CANVAS — the frame the drawing sits in (D4, 2026-09-04).
 *
 * §10 of the 4 Sep audit: at a real portfolio shape the diagram ran off
 * the right edge of the card with no scroll, no fit and no minimap (atlas
 * 92, twelve instruments; 84, the co-owned DAG's fifth column). The
 * drawing itself was not the defect — the frame was missing.
 *
 * This wraps whichever renderer the Overview chose (the fan-bar tree or
 * the co-owned/wide canvas) and gives it:
 *   · FIT TO WIDTH on load — the drawing is scaled to the card, never
 *     magnified past 1:1;
 *   · a SCROLLER when even the fit runs past the card, with the minimap
 *     carrying the viewport rectangle (click it to move the view);
 *   · the zoom control − · N % · + with `fit` and `1:1`, and the same
 *     three moves on the keyboard: `+`, `−`, `0`.
 * The chrome only appears when there is something to fit: a chain that
 * stands at 1:1 inside its card keeps the plain surface it has today.
 *
 * The geometry is pure and unit-tested in `chainCanvas.ts`; this file is
 * measurement, state and markup. Focus lighting and the drawer are NOT
 * touched — the canvas never reaches inside the drawing. */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  CANVAS,
  fitScale,
  lensCaption,
  miniFit,
  miniViewport,
  overflows,
  pctLabel,
  scrollFromMini,
  zoomKey,
  zoomTo,
} from './chainCanvas'

interface Size {
  w: number
  h: number
}
interface MiniBox {
  x: number
  y: number
  w: number
  h: number
  kind: string
}

const same = (a: Size, b: Size) => Math.abs(a.w - b.w) < 1 && Math.abs(a.h - b.h) < 1

export default function ChainCanvas({ children }: { children: React.ReactNode }) {
  const viewRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [natural, setNatural] = useState<Size>({ w: 0, h: 0 })
  const [view, setView] = useState<Size>({ w: 0, h: 0 })
  const [boxes, setBoxes] = useState<MiniBox[]>([])
  const [scroll, setScroll] = useState({ left: 0, top: 0 })
  /* `fit` is the default lens; a press of −/+/0 (or the 1:1 chip) pins an
   * explicit scale until `fit` is chosen again. */
  const [pinned, setPinned] = useState<number | null>(null)

  const fit = fitScale(natural.w, view.w)
  const scale = pinned ?? fit
  /* The frame's chrome shows when the drawing does not stand at 1:1 in its
   * card, or once the officer has taken the zoom into their own hands. */
  const framed =
    (natural.w > 0 && view.w > 0 && natural.w + CANVAS.padX > view.w) || pinned !== null

  const measure = useCallback(() => {
    const c = contentRef.current
    const v = viewRef.current
    if (!c || !v) return
    const nat = { w: c.offsetWidth, h: c.offsetHeight }
    const vw = { w: v.clientWidth, h: v.clientHeight }
    setNatural((prev) => (same(prev, nat) ? prev : nat))
    setView((prev) => (same(prev, vw) ? prev : vw))
    /* The minimap is a plan of the drawing's own boxes, read off the DOM so
     * it works for either renderer. Rects come back at the drawn scale, so
     * they are divided back to the drawing's own coordinates. */
    const base = c.getBoundingClientRect()
    const k = base.width && nat.w ? base.width / nat.w : 1
    const next: MiniBox[] = []
    for (const el of Array.from(c.querySelectorAll('[data-testid="graph-box"]'))) {
      const r = el.getBoundingClientRect()
      next.push({
        x: (r.left - base.left) / k,
        y: (r.top - base.top) / k,
        w: r.width / k,
        h: r.height / k,
        kind: (el as HTMLElement).dataset.kind ?? 'entity',
      })
    }
    setBoxes((prev) =>
      prev.length === next.length &&
      prev.every((p, i) => Math.abs(p.x - next[i].x) < 1 && Math.abs(p.y - next[i].y) < 1)
        ? prev
        : next
    )
  }, [])

  useLayoutEffect(() => {
    measure()
  })

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => measure())
    if (viewRef.current) ro.observe(viewRef.current)
    if (contentRef.current) ro.observe(contentRef.current)
    return () => ro.disconnect()
  }, [measure])

  const onKeyDown = (e: React.KeyboardEvent) => {
    const move = zoomKey(e.key)
    if (!move) return
    e.preventDefault()
    if (move === 'reset') setPinned(1)
    else setPinned(zoomTo(scale, move === 'in' ? 1 : -1))
  }

  const mini = miniFit(natural.w, natural.h)
  const rect = miniViewport(
    { scrollLeft: scroll.left, scrollTop: scroll.top, viewW: view.w, viewH: view.h, scale },
    natural,
    mini
  )
  const scrolls = overflows(natural.w, view.w, scale)

  const jump = (e: React.MouseEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    const to = scrollFromMini(
      { x: e.clientX - r.left, y: e.clientY - r.top },
      { viewW: view.w, viewH: view.h, scale },
      natural,
      mini
    )
    viewRef.current?.scrollTo({ left: to.left, top: to.top })
  }

  return (
    <div
      className={`fa-canvas${framed ? ' framed' : ''}`}
      data-testid="chain-canvas"
      data-scale={framed ? Math.round(scale * 100) : undefined}
      role={framed ? 'group' : undefined}
      aria-label={framed ? 'Chain canvas — zoom with plus, minus and zero' : undefined}
      tabIndex={framed ? 0 : undefined}
      onKeyDown={onKeyDown}
    >
      <div
        className="fa-canvas-view"
        ref={viewRef}
        onScroll={(e) =>
          setScroll({ left: e.currentTarget.scrollLeft, top: e.currentTarget.scrollTop })
        }
      >
        <div
          className="fa-canvas-size"
          style={
            framed
              ? { width: Math.round(natural.w * scale), height: Math.round(natural.h * scale) }
              : undefined
          }
        >
          <div
            className="fa-canvas-content"
            ref={contentRef}
            style={framed ? { transform: `scale(${scale})` } : undefined}
          >
            {children}
          </div>
        </div>
      </div>

      {framed ? (
        <>
          {/* the minimap — a plan of the whole chain with the card's own
              viewport drawn on it; click to move the view there */}
          <div className="fa-canvas-mini" data-testid="chain-minimap">
            <svg
              width={CANVAS.miniW}
              height={CANVAS.miniH}
              viewBox={`0 0 ${CANVAS.miniW} ${CANVAS.miniH}`}
              onClick={jump}
              role="img"
              aria-label="Minimap of the whole chain"
            >
              {boxes.map((b, i) => (
                <rect
                  key={i}
                  className={`mmb ${b.kind === 'instrument' ? 'i' : 'e'}`}
                  x={mini.ox + b.x * mini.k}
                  y={mini.oy + b.y * mini.k}
                  width={Math.max(1.5, b.w * mini.k)}
                  height={Math.max(1, b.h * mini.k)}
                  rx={1}
                />
              ))}
              <rect
                className="mmv"
                data-testid="chain-minimap-viewport"
                x={rect.x}
                y={rect.y}
                width={rect.w}
                height={rect.h}
                rx={2}
              />
            </svg>
          </div>
          <div className="fa-canvas-lens" data-testid="chain-lens">
            {lensCaption(natural.w, view.w, scale)}
          </div>
          <div className="fa-canvas-ctl" data-testid="chain-zoom">
            <div className="grp">
              <button
                type="button"
                onClick={() => setPinned(zoomTo(scale, -1))}
                aria-label="Zoom out"
                data-testid="zoom-out"
              >
                −
              </button>
              <span className="pct" data-testid="zoom-pct">
                {pctLabel(scale)}
              </span>
              <button
                type="button"
                onClick={() => setPinned(zoomTo(scale, 1))}
                aria-label="Zoom in"
                data-testid="zoom-in"
              >
                +
              </button>
            </div>
            <div className="grp">
              <button
                type="button"
                className={pinned === null ? 'on' : undefined}
                onClick={() => setPinned(null)}
                data-testid="zoom-fit"
                aria-pressed={pinned === null}
              >
                fit
              </button>
              <button
                type="button"
                className={pinned === 1 ? 'on' : undefined}
                onClick={() => setPinned(1)}
                data-testid="zoom-one"
                aria-pressed={pinned === 1}
              >
                1:1
              </button>
            </div>
          </div>
        </>
      ) : null}
      {framed && scrolls ? <i className="fa-canvas-edge" aria-hidden="true" /> : null}
    </div>
  )
}
